import { ConditionWrapper, Flex } from '@approvalmax/ui/src/components';
import {
    DndContext,
    DragEndEvent,
    MouseSensor,
    pointerWithin,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FC, memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';

import LineItemsTableHeader from '../LineItemsTableHeader';
import { LineItemsTableRow } from '../LineItemsTableRow';
import { messages } from './LineItemsTable.messages';
import { AddLineIcon, TransparentButtonStyled } from './LineItemsTable.styles';
import { LineItemsTableProps } from './LineItemsTable.types';

const bem = bemFactory.block('reqf-line-items-table');
const qa = bemFactory.qa('reqf-line-items-table');

export const LineItemsTable: FC<LineItemsTableProps> = memo((props) => {
    const {
        tableRowClassName,
        lineItems,
        columnDefinitions,
        onCloneLine,
        onRemoveLine,
        onAddLine,
        canAddLine = true,
        canReorder = true,
        invalid,
        linesNumberReached,
        onReorder,
        className,
    } = props;

    const onRowReorderEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            const oldIndex = lineItems.findIndex((lineItem) => lineItem.id === active.id);
            const newIndex = lineItems.findIndex((lineItem) => lineItem.id === over?.id);

            if (newIndex !== -1 && oldIndex !== newIndex && onReorder) {
                onReorder(oldIndex, newIndex);
            }
        },
        [onReorder, lineItems]
    );

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, { activationConstraint: { distance: 10 } })
    );

    // lineItems var is immutable, so we need to make a copy
    // for the SortableContext
    const lineItemsCopy = useMemo(() => [...lineItems], [lineItems]);

    return (
        <Flex className={bem.add(className)()} direction='column' data-qa={qa()} spacing='0' overflow='auto'>
            <LineItemsTableHeader columnDefinitions={columnDefinitions} lineItems={lineItems} />

            <ConditionWrapper
                condition={canReorder}
                wrapperIf={(children) => (
                    <DndContext
                        sensors={sensors}
                        modifiers={[restrictToVerticalAxis]}
                        collisionDetection={pointerWithin}
                        onDragEnd={onRowReorderEnd}
                    >
                        <SortableContext items={lineItemsCopy} strategy={verticalListSortingStrategy}>
                            {children}
                        </SortableContext>
                    </DndContext>
                )}
            >
                <Flex direction='column' spacing='0'>
                    {lineItems.map((lineItem, index) => (
                        <LineItemsTableRow
                            key={lineItem.id}
                            className={tableRowClassName}
                            lineItem={lineItem}
                            lineItemIndex={index}
                            columnDefinitions={columnDefinitions}
                            onCloneLine={onCloneLine}
                            onRemoveLine={onRemoveLine}
                            invalid={invalid}
                            canReorder={canReorder}
                        />
                    ))}
                </Flex>
            </ConditionWrapper>

            {canAddLine && onAddLine && (
                <TransparentButtonStyled
                    qa={qa('add-line-button')}
                    execute={onAddLine}
                    disabled={linesNumberReached}
                    title={linesNumberReached ? messages.linesNumberReachedText : undefined}
                >
                    <AddLineIcon className={bem('add-line-icon')} width={16} height={16} />

                    {messages.addLineButtonText}
                </TransparentButtonStyled>
            )}
        </Flex>
    );
});

LineItemsTable.displayName = 'LineItemsTable';
